<script setup>
import { computed, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  getVoiceCallProvider,
  INBOX_TYPES,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import EvolutionApi from 'dashboard/api/channel/evolutionApi';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const VIDEO_FEATURES = {
  WHATSAPP: 'talkvia_video_whatsapp',
  REALTIMEKIT: 'talkvia_video_realtimekit',
  GOOGLE_MEET: 'talkvia_video_google_meet',
};
const videoCallTooltip = 'Iniciar videochamada';

const { t } = useI18n();
const store = useStore();
const callsStore = useCallsStore();
const whatsappCallSession = useWhatsappCallSession();
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const { isCloudFeatureEnabled, currentAccount } = useAccount();
const [showVideoProviders, toggleVideoProviders] = useToggle(false);
const isCreatingVideoLink = ref(false);
const isCreatingRealtimeKitCall = ref(false);
const isCreatingGoogleMeetCall = ref(false);

const voiceCallProvider = computed(() => getVoiceCallProvider(props.inbox));
const isVoiceCallInbox = computed(
  () =>
    voiceCallProvider.value !== null &&
    isCloudFeatureEnabled(FEATURE_FLAGS.CHANNEL_VOICE)
);
const isWhatsappVoiceInbox = computed(
  () => voiceCallProvider.value === VOICE_CALL_PROVIDERS.WHATSAPP
);
const isEvolutionVideoInbox = computed(() => {
  const channelType = props.inbox?.channel_type || props.inbox?.channelType;
  const attributes =
    props.inbox?.additional_attributes || props.inbox?.additionalAttributes;

  return (
    channelType === INBOX_TYPES.API &&
    attributes?.is_evolution_api_integration === true
  );
});

const isVideoFeatureEnabled = feature =>
  currentAccount.value?.features?.[feature] === true;

const videoProviderItems = computed(() => {
  const items = [];

  if (
    isEvolutionVideoInbox.value &&
    isVideoFeatureEnabled(VIDEO_FEATURES.WHATSAPP)
  ) {
    items.push({
      icon: 'i-lucide-message-circle',
      label: 'Vídeo pelo WhatsApp',
      action: 'whatsapp',
      value: 'whatsapp',
    });
  }

  if (
    isEvolutionVideoInbox.value &&
    isVideoFeatureEnabled(VIDEO_FEATURES.REALTIMEKIT)
  ) {
    items.push({
      icon: 'i-lucide-cloud',
      label: 'Cloudflare RealtimeKit',
      action: 'realtimekit',
      value: 'realtimekit',
    });
  }

  if (isVideoFeatureEnabled(VIDEO_FEATURES.GOOGLE_MEET)) {
    items.push({
      icon: 'i-lucide-video',
      label: 'Google Meet',
      action: 'google_meet',
      value: 'google_meet',
    });
  }

  return items;
});

const hasVideoProviders = computed(() => videoProviderItems.value.length > 0);
const isCreatingVideoCall = computed(
  () =>
    isCreatingVideoLink.value ||
    isCreatingRealtimeKitCall.value ||
    isCreatingGoogleMeetCall.value
);

const isCallButtonDisabled = computed(() => {
  if (callsStore.hasActiveCall || callsStore.hasIncomingCall) return true;
  if (isWhatsappVoiceInbox.value) {
    return whatsappCallSession.isInitiating.value;
  }
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const isCallButtonLoading = computed(() =>
  isWhatsappVoiceInbox.value
    ? whatsappCallSession.isInitiating.value
    : !!contactsUiFlags.value?.isInitiatingCall
);

const callButtonTooltip = computed(() =>
  isWhatsappVoiceInbox.value
    ? t('CONVERSATION.HEADER.WHATSAPP_CALL')
    : t('CONVERSATION.HEADER.VOICE_CALL')
);

const startWhatsappCall = async () => {
  if (whatsappCallSession.isInitiating.value) return;
  try {
    const response = await whatsappCallSession.initiateOutboundCall({
      conversationId: props.chat.id,
    });

    if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
    if (!response?.id) {
      const status = response?.status;
      const messageKey =
        status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
          ? 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING'
          : 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED';
      useAlert(t(messageKey));
      return;
    }

    callsStore.addCall({
      callSid: response.call_id,
      callId: response.id,
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED'));
  }
};

const startTwilioCall = async () => {
  if (contactsUiFlags.value?.isInitiatingCall) return;
  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.chat?.meta?.sender?.id,
      inboxId: props.inbox?.id,
      conversationId: props.chat.id,
    });

    callsStore.addCall({
      callSid: response?.call_sid,
      conversationId: response?.conversation_id ?? props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.VOICE_CALL_FAILED'));
  }
};

const startCall = () => {
  if (isWhatsappVoiceInbox.value) return startWhatsappCall();
  return startTwilioCall();
};

const sendVideoInvitation = async (link, provider) => {
  await store.dispatch('createPendingMessageAndSend', {
    conversationId: props.chat.id,
    message: `📹 Entre na videochamada pelo ${provider}:\n${link}`,
    private: false,
  });
};

const startEvolutionVideoCall = async () => {
  if (isCreatingVideoLink.value) return;

  isCreatingVideoLink.value = true;
  try {
    const response = await EvolutionApi.createVideoCallLink(props.inbox.id);
    const link = response?.data?.url;
    if (!link) {
      throw new Error(
        'A conexão WhatsApp não retornou o link da videochamada.'
      );
    }

    await sendVideoInvitation(link, 'WhatsApp');
    useAlert('Convite de videochamada pelo WhatsApp enviado.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        'Não foi possível criar a videochamada pelo WhatsApp.'
    );
  } finally {
    isCreatingVideoLink.value = false;
  }
};

const startHostedVideoCall = async ({
  createCall,
  provider,
  missingLinksMessage,
  fallbackError,
}) => {
  const agentWindow = window.open('about:blank', '_blank');
  if (agentWindow) agentWindow.opener = null;

  try {
    const response = await createCall();
    const { agent_url: agentUrl, customer_url: customerUrl } =
      response?.data || {};
    if (!agentUrl || !customerUrl) throw new Error(missingLinksMessage);

    await sendVideoInvitation(customerUrl, provider);
    if (agentWindow) agentWindow.location.href = agentUrl;
    else window.open(agentUrl, '_blank', 'noopener,noreferrer');
    useAlert(`Convite ${provider} enviado.`);
  } catch (error) {
    if (agentWindow) agentWindow.close();
    useAlert(error?.response?.data?.error || error?.message || fallbackError);
  }
};

const startRealtimeKitVideoCall = async () => {
  if (isCreatingRealtimeKitCall.value) return;

  isCreatingRealtimeKitCall.value = true;
  await startHostedVideoCall({
    createCall: () =>
      EvolutionApi.createRealtimeKitVideoCall(props.inbox.id, props.chat.id),
    provider: 'RealtimeKit',
    missingLinksMessage: 'O RealtimeKit não retornou os links da videochamada.',
    fallbackError: 'Não foi possível criar a sala RealtimeKit.',
  });
  isCreatingRealtimeKitCall.value = false;
};

const startGoogleMeetVideoCall = async () => {
  if (isCreatingGoogleMeetCall.value) return;

  isCreatingGoogleMeetCall.value = true;
  await startHostedVideoCall({
    createCall: () =>
      EvolutionApi.createGoogleMeetVideoCall(props.inbox.id, props.chat.id),
    provider: 'Google Meet',
    missingLinksMessage: 'O Google Agenda não retornou o link do Meet.',
    fallbackError: 'Não foi possível criar a reunião no Google Meet.',
  });
  isCreatingGoogleMeetCall.value = false;
};

const handleVideoProvider = ({ action }) => {
  toggleVideoProviders(false);

  if (action === 'whatsapp') return startEvolutionVideoCall();
  if (action === 'realtimekit') return startRealtimeKitVideoCall();
  if (action === 'google_meet') return startGoogleMeetVideoCall();
  return undefined;
};
</script>

<template>
  <div v-if="isVoiceCallInbox || hasVideoProviders" class="flex items-center">
    <NextButton
      v-if="isVoiceCallInbox"
      v-tooltip.bottom="callButtonTooltip"
      sm
      ghost
      slate
      icon="i-lucide-phone"
      :is-loading="isCallButtonLoading"
      :disabled="isCallButtonDisabled"
      @click="startCall"
    />

    <div
      v-if="hasVideoProviders"
      v-on-clickaway="() => toggleVideoProviders(false)"
      class="relative flex items-center"
    >
      <NextButton
        v-tooltip.bottom="videoCallTooltip"
        sm
        ghost
        slate
        icon="i-lucide-video"
        :is-loading="isCreatingVideoCall"
        :disabled="isCreatingVideoCall"
        :aria-label="videoCallTooltip"
        @click="toggleVideoProviders()"
      />
      <DropdownMenu
        v-if="showVideoProviders"
        :menu-items="videoProviderItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full min-w-56"
        @action="handleVideoProvider"
      />
    </div>
  </div>
  <template v-else />
</template>
